import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { usePlanning } from '../contexts/PlanningContext';
import { ActionItem } from '../types/planning.types';
import { format, isAfter, isBefore, isToday, addDays } from 'date-fns';
import { he } from 'date-fns/locale';

type FilterType = 'all' | 'pending' | 'completed' | 'overdue' | 'today' | 'week';

interface FlatActionItem extends ActionItem {
  planId: string;
  planTitle: string;
  subjectId: string;
  subjectTitle: string;
}

export default function ActionItemsPage() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { plans, toggleActionItem } = usePlanning();
  const [filter, setFilter] = useState<FilterType>('pending');
  const [sortBy, setSortBy] = useState<'dueDate' | 'priority' | 'plan'>('dueDate');
  const [searchQuery, setSearchQuery] = useState('');

  // Flatten all action items
  const allItems = useMemo(() => {
    const items: FlatActionItem[] = [];
    plans.forEach(plan => {
      plan.subjects.forEach(subject => {
        subject.actionItems.forEach(item => {
          items.push({
            ...item,
            planId: plan.id,
            planTitle: plan.title,
            subjectId: subject.id,
            subjectTitle: subject.title,
          });
        });
      });
    });
    return items;
  }, [plans]);

  // Filter items
  const filteredItems = useMemo(() => {
    const now = new Date();
    const weekFromNow = addDays(now, 7);

    return allItems.filter(item => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !item.description.toLowerCase().includes(query) &&
          !item.assigneeName.toLowerCase().includes(query) &&
          !item.planTitle.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      // Status filter
      switch (filter) {
        case 'pending':
          return !item.isCompleted;
        case 'completed':
          return item.isCompleted;
        case 'overdue':
          return !item.isCompleted && item.dueDate && isBefore(new Date(item.dueDate), now);
        case 'today':
          return item.dueDate && isToday(new Date(item.dueDate));
        case 'week':
          return item.dueDate &&
                 isAfter(new Date(item.dueDate), now) &&
                 isBefore(new Date(item.dueDate), weekFromNow);
        default:
          return true;
      }
    });
  }, [allItems, filter, searchQuery]);

  // Sort items
  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      switch (sortBy) {
        case 'dueDate':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'plan':
          return a.planTitle.localeCompare(b.planTitle, 'he');
        default:
          return 0;
      }
    });
  }, [filteredItems, sortBy]);

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const handleToggle = async (item: FlatActionItem) => {
    await toggleActionItem(item.planId, item.subjectId, item.id);
  };

  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: allItems.length,
      pending: allItems.filter(i => !i.isCompleted).length,
      completed: allItems.filter(i => i.isCompleted).length,
      overdue: allItems.filter(i => !i.isCompleted && i.dueDate && isBefore(new Date(i.dueDate), now)).length,
    };
  }, [allItems]);

  return (
    <div className="action-items-page">
      <div className="page-header">
        <h1 className="page-title">{t('planning.actionItemsTitle')}</h1>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => setFilter('all')} style={{ cursor: 'pointer' }}>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">{t('planning.totalTasks')}</div>
        </div>
        <div className="stat-card" onClick={() => setFilter('pending')} style={{ cursor: 'pointer' }}>
          <div className="stat-value" style={{ color: 'var(--primary-color)' }}>{stats.pending}</div>
          <div className="stat-label">{t('planning.pending')}</div>
        </div>
        <div className="stat-card" onClick={() => setFilter('completed')} style={{ cursor: 'pointer' }}>
          <div className="stat-value" style={{ color: 'var(--success-color)' }}>{stats.completed}</div>
          <div className="stat-label">{t('planning.filterCompleted')}</div>
        </div>
        <div className="stat-card" onClick={() => setFilter('overdue')} style={{ cursor: 'pointer' }}>
          <div className="stat-value" style={{ color: 'var(--error-color)' }}>{stats.overdue}</div>
          <div className="stat-label">{t('planning.overdue')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder={t('planning.searchTasks')}
          className="search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="filter-group">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterType)}
          >
            <option value="all">{t('planning.filterAll')}</option>
            <option value="pending">{t('planning.filterPending')}</option>
            <option value="completed">{t('planning.filterCompleted')}</option>
            <option value="overdue">{t('planning.filterOverdue')}</option>
            <option value="today">{t('planning.filterToday')}</option>
            <option value="week">{t('planning.filterWeek')}</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          >
            <option value="dueDate">{t('planning.sortByDate')}</option>
            <option value="priority">{t('planning.sortByPriority')}</option>
            <option value="plan">{t('planning.sortByPlan')}</option>
          </select>
        </div>
      </div>

      {/* Action Items List */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {sortedItems.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--spacing-xl)' }}>
              <div className="empty-state-icon">✅</div>
              <h3 className="empty-state-title">
                {filter === 'pending' ? t('planning.noPendingTasks') :
                 filter === 'completed' ? t('planning.noCompletedTasks') :
                 filter === 'overdue' ? t('planning.noOverdueTasks') :
                 t('planning.noTasks')}
              </h3>
            </div>
          ) : (
            <div className="action-items-list">
              {sortedItems.map(item => (
                <div
                  key={`${item.planId}-${item.subjectId}-${item.id}`}
                  className={`action-item ${item.isCompleted ? 'completed' : ''}`}
                >
                  <div
                    className={`action-checkbox ${item.isCompleted ? 'checked' : ''}`}
                    onClick={() => handleToggle(item)}
                  >
                    {item.isCompleted && '✓'}
                  </div>
                  <div className="action-content" style={{ flex: 1 }}>
                    <div className="action-description">{item.description}</div>
                    <div className="action-meta">
                      <span
                        className="action-plan-link"
                        style={{ cursor: 'pointer', color: 'var(--primary-color)' }}
                        onClick={() => navigate(`/plans/${item.planId}`)}
                      >
                        📋 {item.planTitle}
                      </span>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        • {item.subjectTitle}
                      </span>
                      {item.assigneeName && (
                        <span className="action-assignee">
                          👤 {item.assigneeName}
                        </span>
                      )}
                      {item.dueDate && (
                        <span className={`action-due-date ${isOverdue(item.dueDate) && !item.isCompleted ? 'overdue' : ''}`}>
                          📅 {format(new Date(item.dueDate), 'dd/MM/yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`priority-badge ${item.priority}`}>
                    {item.priority === 'high' && t('planning.priorityHigh')}
                    {item.priority === 'medium' && t('planning.priorityMedium')}
                    {item.priority === 'low' && t('planning.priorityLow')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
